/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Theme } from '@coursera/cds-core';
import { Typography2, useTheme } from '@coursera/cds-core';
import { CheckIcon, ErrorIcon, InformativeIcon, WarningIcon } from '@coursera/cds-icons';

const styles = {
  plagiarisimDetectionResultLayout: (type: ResultType, theme: Theme) => {
    let bgColor = '';
    switch (type) {
      case 'not_detected':
        bgColor = 'var(--cds-color-green-25)';
        break;
      case 'detected':
        bgColor = 'var(--cds-color-red-25)';
        break;
      case 'error':
        bgColor = 'var(--cds-color-yellow-50)';
        break;
      case 'retry':
        bgColor = 'var(--cds-color-blue-25)';
        break;
      default:
        bgColor = 'undefined';
    }
    return css({
      alignItems: 'baseline',
      display: 'flex',
      minHeight: 56,
      backgroundColor: bgColor,
    });
  },
  notDetected: () =>
    css({
      verticalAlign: 'middle',
    }),
  detected: () =>
    css({
      verticalAlign: 'middle',
    }),
  error: (theme: Theme) =>
    css({
      color: 'var(--cds-color-yellow-200)',
      verticalAlign: 'middle',
    }),
  retry: () =>
    css({
      verticalAlign: 'middle',
    }),
  leftContainer: (theme: Theme) =>
    css({
      marginLeft: 'var(--cds-spacing-300)',
    }),
  rightContainer: (theme: Theme) =>
    css({
      marginLeft: 'var(--cds-spacing-200)',
      padding: 'var(--cds-spacing-200) 0',
    }),
  children: (theme: Theme) =>
    css({
      marginTop: 'var(--cds-spacing-100)',
    }),
};

export const RESULT_TYPES = {
  RETRY: 'retry',
  DETECTED: 'detected',
  ERROR: 'error',
  NOT_DETECTED: 'not_detected',
} as const;

export type ResultType = (typeof RESULT_TYPES)[keyof typeof RESULT_TYPES];

type Props = {
  title: React.ReactNode;
  type: ResultType;
};

const getResultIcon = (resultType: ResultType) => {
  switch (resultType) {
    case RESULT_TYPES.RETRY:
      return <InformativeIcon size="medium" css={styles.retry} />;
    case RESULT_TYPES.DETECTED:
      return <WarningIcon size="medium" css={styles.detected} />;
    case RESULT_TYPES.ERROR:
      return <ErrorIcon size="medium" css={styles.error} />;
    case RESULT_TYPES.NOT_DETECTED:
      return <CheckIcon size="medium" css={styles.notDetected} />;
    default:
      return null;
  }
};

/**
 * Component primarily for managing different coloring/icons of different types of plagiarism results
 */
const PlagiarismDetectionResultLayout: React.LegacyFunctionComponentWithChildren<Props> = (props) => {
  const { title, type, children } = props;
  const theme = useTheme();

  return (
    <div css={styles.plagiarisimDetectionResultLayout(type, theme)}>
      <div css={styles.leftContainer}>
        <div data-testid="icon">{getResultIcon(type)}</div>
      </div>
      <div css={styles.rightContainer}>
        <Typography2 component="h3" variant="subtitleMedium">
          {title}
        </Typography2>
        {children && <div css={styles.children}>{children}</div>}
      </div>
    </div>
  );
};

export default PlagiarismDetectionResultLayout;
